<template>
  <div
    ref="el"
    class="AtlasWidget AtlasWidget--v2"
    :class="[
      lazyLoading && !isInViewport ? 'AtlasWidget--lazy' : '',
      isDataSummarySkin ? 'AtlasWidget-border-wrap' : '',
    ]"
  >
    <template v-if="isSupported && isInViewport">
      <atlas-indicator-controls
        v-if="!disableCategoriesLinks && geoControls"
        :indicator-id="indicatorId"
        :breakdown-slug="embeddableContext.query.breakdown || breakdown"
        :categories="indicatorCategories"
        :links="indicatorLinks"
        :geo-controls="geoControls"
        :is-geo-compare-default-enabled="defaultGeoComparison"
        class="AtlasWidget-controls"
      />

      <atlas-params>
        <template v-slot:default="params">
          <atlas-indicator
            :indicator-id="indicatorId"
            :breakdown-id="embeddableContext.query.breakdown || breakdown"
            :geo-persistence="geoPersistence"
            :display-mode="displayMode"
            :args="params.args"
            :geo-code="params.geoCode"
            :geo-compare-code="params.geoCompareCode ?? this.secondaryGeo"
            :geo-type="params.geoType"
            :geo-complexity="params.geoComplexity"
            :tenant="tenant"
            :disabledDownloadImage="disabledDownloadImage"
            :disabledDownloadPowerpoint="disabledDownloadPowerpoint"
            :disabledDownloadXlsx="disabledDownloadXlsx"
            @setDefaultGeoCompareState="handleDefaultGeoComparisonState"
          />
        </template>
      </atlas-params>

      <atlas-tour />
    </template>

    <atlas-unsupported v-else />
  </div>
</template>

<script>
import {
  defineComponent,
  computed,
  ref,
  onMounted,
  inject,
  onBeforeMount,
} from 'vue';
import { mapState, mapMutations, mapGetters, useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';

import { EmbeddableContextInjectionKey } from '../../embedding';

import AtlasUnsupported from './unsupported.vue';
import AtlasIndicatorControls from './indicator-controls.vue';
import AtlasIndicator from './indicator.vue';
import AtlasParams from './params.vue';
import AtlasTour from './tour.vue';
import { SKIN_KEYS } from '../../constants';

export default defineComponent({
  name: 'atlas-widget-v2',

  components: {
    AtlasIndicatorControls,
    AtlasIndicator,
    AtlasUnsupported,
    AtlasParams,
    AtlasTour,
  },

  props: {
    categories: {
      type: Array,
    },

    links: {
      type: Array,
    },

    indicator: {
      type: String,
    },

    breakdown: {
      type: String,
    },

    /**
     * Controls visibility of geo select and compare controls
     * if allowGeoCompare is enabled for breakdown.
     */
    geoControls: Boolean,
    geoPersistence: {
      type: Boolean,
      default: false,
    },

    theme: {
      type: String,
      default() {
        // Determine default theme based on page body class.
        const defaultTheme = 'nea';
        const classDomainTheme = {
          'domain-bayareaequityatlas-org': 'baea',
          'domain-immigrantdataca-org': 'cidp',
        };
        const classDomain = Object.keys(classDomainTheme).find((c) =>
          document.body.classList.contains(c),
        );
        const theme = classDomain
          ? classDomainTheme[classDomain]
          : defaultTheme;
        return theme;
      },
    },

    skin: {
      type: String,
    },
    mode: {
      type: String,
      default: 'app',
    },

    displayGroupRevision: {
      type: Number,
    },
    datasetRevision: {
      type: Number,
      default: 1,
    },

    tenant: {
      type: String,
    },

    apiBaseUrl: {
      type: String,
    },

    lazyLoading: Boolean,

    disableCategoriesLinks: Boolean,
    disabledDownloadImage: Boolean,
    disabledDownloadPowerpoint: Boolean,
    disabledDownloadXlsx: Boolean,
  },

  provide() {
    return {
      widgetContext: computed(() => ({
        displayGroupRevision: this.displayGroupRevision,
        skin: this.skin,
      })),
    };
  },

  computed: {
    ...mapState('ui', ['isSupported']),
    ...mapState('breakdown', ['secondaryGeo']),
    ...mapGetters('indicator', [
      'defaultIndicator',
      'activeGeoCode',
      'activeCompareGeoCode',
    ]),
    ...mapGetters('indicator', ['indicatorConfig']),
    ...mapGetters('breakdown', ['secondaryGeo']),

    displayMode() {
      return this.mode;
    },
    activeBreakdown() {
      return this.breakdownById(this.breakdown);
    },
    indicatorId() {
      return this.indicator || this.defaultIndicator;
    },
    isDataSummarySkin() {
      const currentSkin = this.skin || this.indicatorConfig?.skin;
      return currentSkin === SKIN_KEYS.DATA_SUMMARY;
    },

    query() {
      return (this.$route || {}).query || {};
    },

    selectedGeoCodes() {
      return [
        this.query?.geo || this.activeGeoCode,
        this.query?.geo_compare || this.activeCompareGeoCode,
      ].filter((item) => !!item);
    },
  },

  watch: {
    theme: {
      immediate: true,
      handler(theme) {
        this.setTheme({ theme });
      },
    },

    tenant: {
      immediate: true,
      handler(tenantKey) {
        if (tenantKey) this.$api.setTenant(tenantKey);
      },
    },

    apiBaseUrl: {
      immediate: true,
      handler(apiBaseUrl) {
        if (apiBaseUrl) this.$api.setBaseURL(apiBaseUrl);
      },
    },
    selectedGeoCodes: {
      immediate: true,
      handler(data) {
        this.$emit('update:selectedGeoCodes', data);
      },
    },
  },

  methods: {
    ...mapMutations('display', ['setTheme']),

    handleDefaultGeoComparisonState(isDefaultGeoComparisonEnabled) {
      this.defaultGeoComparison =
        isDefaultGeoComparisonEnabled && this.secondaryGeo?.id !== undefined;
    },
  },
  emits: ['update:geo', 'update:selectedGeoCodes'],

  setup(props) {
    const store = useStore();

    const el = ref(null);
    const isInViewport = ref(!props.lazyLoading || false);
    let intersectionObserver = null;

    const indicatorCategories = computed(() => {
      if (props.disableCategoriesLinks) return null;
      return props.categories && props.categories.length
        ? props.categories
        : window.Drupal?.settings?.indicatorCategories;
    });

    const indicatorLinks = computed(() => {
      if (props.disableCategoriesLinks) return null;
      return props.links && props.links.length
        ? props.links
        : window.Drupal?.settings?.indicatorLinks;
    });

    const handleIntersection = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          isInViewport.value = true;

          intersectionObserver.unobserve(entry.target);
        }
      });
    };
    const intersectionObserverOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.25,
    };
    intersectionObserver = new IntersectionObserver(
      handleIntersection,
      intersectionObserverOptions,
    );

    const embeddableContext = inject(
      EmbeddableContextInjectionKey,
      () => ({ query: {} }),
      true,
    );

    const exploreEmbeddableContext = () => {
      // restore geo compare option
      // it should not be persistent for different displays
      if (embeddableContext.query) {
        const geoCompareCode = embeddableContext.query?.geo_compare;
        store.dispatch('indicator/updateCompareGeoCode', geoCompareCode);
      }
    };

    const defaultGeoComparison = ref(false);

    onBeforeMount(() => {
      if (embeddableContext) {
        exploreEmbeddableContext(embeddableContext);
      }
    });

    onMounted(() => {
      // Observe widget for lazy loading mode
      if (props.lazyLoading) intersectionObserver.observe(el.value);
    });

    return {
      embeddableContext,
      el,
      isInViewport,
      indicatorCategories,
      indicatorLinks,
      defaultGeoComparison,
    };
  },
});
</script>

<style lang="scss">
.AtlasWidget {
  box-sizing: border-box;

  *,
  *::before,
  *::after {
    box-sizing: inherit;
  }

  &-controls {
    @media screen and (min-width: 1100px) {
      margin: 0 15px 40px;
    }
  }

  &-notSupported {
    padding: 200px 30px;
    background-color: #f0f0f0;
    text-align: center;
  }

  &--v2 {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  &--lazy {
    min-height: 100vh;
  }

  &-border-wrap {
    border-top: solid 1px #b5c0cd;
  }
}
</style>
